// Recovery for stale module graphs in the browser.
//
// When a new build is deployed (or Vite re-optimizes dependencies) a tab can end
// up holding a half-old module graph: some modules point at the previous React
// copy, others at the new one. React then renders with a null dispatcher and the
// page blanks out with errors like:
//   "null is not an object (evaluating 'resolveDispatcher().use')"
//   "Invalid hook call" / "Cannot read properties of null (reading 'use')"
// The only correct fix in the tab is a single hard reload.

const RELOAD_KEY = "__cario_stale_module_reload";
const RELOAD_COUNT_KEY = "__cario_stale_module_reload_count";
const RELOAD_WINDOW_MS = 30_000;
const MAX_RELOADS = 2;

const STALE_PATTERNS =
  /resolveDispatcher|Invalid hook call|reading 'use'|reading 'useState'|Importing a module script failed|Failed to fetch dynamically imported module|Loading chunk|ChunkLoadError|dynamically imported module|outdated optimize dep/i;

export function isStaleModuleError(message: string | undefined | null): boolean {
  return !!message && STALE_PATTERNS.test(message);
}

/**
 * Reloads at most twice, and never more than once per 30s, so a genuine bug
 * can never trap the tab in a reload loop (which looks like a frozen app).
 * The reload is cache-busted so the browser fetches the new build's assets.
 */
export function reloadOnceForStaleModules(): boolean {
  if (typeof window === "undefined") return false;
  let count = 0;
  try {
    const previous = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    count = Number(sessionStorage.getItem(RELOAD_COUNT_KEY) ?? 0);
    if (count >= MAX_RELOADS) return false;
    if (previous && Date.now() - previous < RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    sessionStorage.setItem(RELOAD_COUNT_KEY, String(count + 1));
  } catch {
    // Private mode / blocked storage: still attempt one reload.
  }
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("_b", Date.now().toString(36));
    window.location.replace(url.toString());
    return true;
  } catch {
    window.location.reload();
    return true;
  }
}

/** Clears the loop guard after the app has rendered successfully. */
export function clearStaleModuleGuard() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(RELOAD_KEY);
    sessionStorage.removeItem(RELOAD_COUNT_KEY);
  } catch { /* ignore */ }
}


let installed = false;

/** Installs global listeners that self-heal a blank screen caused by stale modules. */
export function installStaleModuleRecovery() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const handle = (message: unknown) => {
    if (typeof message === "string" && isStaleModuleError(message)) {
      reloadOnceForStaleModules();
    }
  };

  window.addEventListener("error", (event) => {
    const err = (event as ErrorEvent).error;
    handle(err instanceof Error ? err.message : (event as ErrorEvent).message);
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = (event as PromiseRejectionEvent).reason;
    handle(reason instanceof Error ? reason.message : String(reason ?? ""));
  });
}
